import "@/styles/root.css";

import { useEffect } from "react";
import { ErrorBoundary as ReactErrorBoundary } from "react-error-boundary";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { useMe } from "@/api/me";
import ErrorBoundary, {
  FallbackComponent,
} from "@/components/layout/error-boundary";
import HeaderBar from "@/components/layout/header-bar";
import { useAuth } from "@/contexts/auth-context";
import { cn } from "@/lib/utils";
import type { Menu } from "@/types/menu";
import { isDev } from "@/utils/env";
import { authenticatedMenu } from "@/utils/menu";
import { testIsCurrentUrl } from "@/utils/submenu";

interface MenuResult {
  currentMenuIndex: number;
  label: string;
}

const findCurrentMenu = (
  menuList: Array<Menu>,
  currentPath: string
): MenuResult | undefined => {
  for (let index = 0; index < menuList.length; index++) {
    const menu = menuList[index];
    let specialLabel: string | undefined;

    const currentSubMenu = menu.subList.find((subMenu) => {
      const result = testIsCurrentUrl(
        subMenu.path,
        currentPath,
        subMenu.alternativePaths
      );
      if (typeof result === "string") specialLabel = result;
      return !!result;
    });

    if (currentSubMenu) {
      return {
        currentMenuIndex: index,
        label: specialLabel ?? currentSubMenu.label,
      };
    }

    if (menu.path === currentPath) {
      return {
        currentMenuIndex: index,
        label: menu.label,
      };
    }
  }
  return undefined;
};

const Layout = () => {
  const navigate = useNavigate();

  const { setUser } = useAuth();
  const { data: me, isLoading } = useMe();

  const location = useLocation();
  const currentPath = location.pathname;

  const { currentMenuIndex } = findCurrentMenu(
    authenticatedMenu,
    currentPath
  ) || {
    currentMenuIndex: -1,
    label: "",
  };

  useEffect(() => {
    if (currentMenuIndex === -1) {
      if (isDev) {
        console.warn("Route not found in menu", currentPath);
      }
      const firstRoute = authenticatedMenu[0];
      if (!firstRoute) {
        navigate("/404", { replace: true });
      }
      const firstSubRoute = firstRoute.subList?.[0];
      navigate(firstSubRoute ? firstSubRoute.path : firstRoute.path, {
        replace: true,
      });
    }
  }, [currentMenuIndex, navigate, currentPath]);

  useEffect(() => {
    if (!isLoading) setUser(me || null);
  }, [isLoading, me, setUser]);

  return (
    <div className="flex flex-col min-w-full lg:flex-row">
      {/* Fixed header bar that spans full width */}
      <HeaderBar />

      {/* Main content area with horizontal scroll and top padding for fixed header */}
      <div
        className={cn([
          "flex flex-col flex-grow min-h-0 w-full pt-[52px]",
          currentPath === "/dashboard" || currentPath.match(/\/patients\/[\d]/)
            ? "bg-gray-50"
            : "bg-white",
        ])}
      >
        <div
          className={cn([
            "flex flex-col items-center w-full min-h-0 overflow-y-auto",
            currentPath.match(/\/patients\/[\d]/)
              ? undefined
              : "px-4 py-8 gap-6 sm:gap-6 sm:px-4 sm:py-4",
          ])}
        >
          <ReactErrorBoundary FallbackComponent={FallbackComponent}>
            <Outlet />
          </ReactErrorBoundary>
        </div>
      </div>
    </div>
  );
};

const LayoutWithErrorBoundary = () => {
  return (
    <ErrorBoundary>
      <Layout />
    </ErrorBoundary>
  );
};

export default LayoutWithErrorBoundary;
